import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
    Box,
    Button,
    IconButton,
    Typography,
    FormGroup,
    FormControlLabel,
    Switch,
} from '@mui/material'

import SelectAllIcon from '@mui/icons-material/SelectAll'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { motion, AnimatePresence } from 'framer-motion'

import Footer from './Footer'
import StatementList from './StatementList'
import MatchNavigator from './MatchNavigator'
import { useMatchNavigation } from './useMatchNavigation'
import { useZustand } from '../../hooks/useZustand'

// Where the statements came from, by the graph's stored type. Only types
// with one unambiguous origin are named; the rest show the title alone.
const SOURCE_LABELS = {
    PDF: 'imported PDF',
    TXT: 'imported text file',
    MD: 'imported Markdown',
    CSV: 'imported spreadsheet',
    JSON: 'imported JSON',
    GOOGLE: 'Google search results',
    AI: 'AI-generated text',
    TWITTER: 'X posts',
    RSS: 'RSS feed',
    SCIENCE: 'research papers',
    EVERNOTE: 'Evernote notes',
    MEMORY: 'AI memory',
}
import {
    useContextsStore,
    findContextById,
    selectIsPublicView,
} from '../../stores/useContextsStore'
import { useRecalculateCurrentGraph } from '../../hooks/useRecalculateCurrentGraph'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import { promoteSeededContext } from '../../stores/bootstrap'

import { useSnackbar } from '../Snackbar'

import { useFilterStore } from '../../stores/useFilterStore'

import { applyAiTopicsToActiveGraph } from '../../utils/applyAiTopics'

import { sendToAnalytics } from '../../Analytics'
import { UserContext } from '../../UserContext'

const selectorShowSnackbar = state => state.showSnackbar
const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore
const currentContextSelector = state => state.currentContext
const autoAddFromAiChatSelector = state => state.autoAddFromAiChat

const SHOW_FILTERED_ONLY_STORAGE_KEY = 'infranodus_show_filtered_only'

const readShowFilteredOnlyFromStorage = () => {
    try {
        const saved = localStorage.getItem(SHOW_FILTERED_ONLY_STORAGE_KEY)
        if (saved === null) return true
        return saved === '1'
    } catch {
        return true
    }
}

const StatementEditor = ({
    setIsOpen,
    isCompareMode,
    isSynthesisMode,
    isSearchMode,
}) => {
    // Use global selectedStatements from filter store
    const selectedStatements = useFilterStore(state => state.selectedStatements)
    const setSelectedStatements = useFilterStore(
        state => state.setSelectedStatements
    )
    const [allStatements, setAllStatements] = useState([])
    const pinnedNodes = useFilterStore(state => state.pinnedNodes)
    const resetPinnedNodes = useFilterStore(state => state.resetPinnedNodes)

    const { username } = React.useContext(UserContext)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)
    const currentContext = useZustand(currentContextSelector)
    const sourceLabel = useZustand(
        state => SOURCE_LABELS[state.currentContextType] || null
    )
    const autoAddFromAiChat = useZustand(autoAddFromAiChatSelector)
    const recalculateCurrentGraph = useRecalculateCurrentGraph()

    // authoring (add/edit/delete) is off in aggregate views and on someone
    // else's public graph
    const isPublicView = useContextsStore(selectIsPublicView)
    const isAuthoringDisabled =
        isCompareMode || isSynthesisMode || isSearchMode || isPublicView

    useEffect(() => {
        const saveStatementToGraph = ({
            text,
            tags,
            extractEntities,
            source,
        }) => {
            handleSave({ text, tags, extractEntities, source })
        }

        if (window.myEventEmitter) {
            myEventEmitter.on('saveToGraph', saveStatementToGraph)
        }

        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener(
                    'saveToGraph',
                    saveStatementToGraph
                )
            }
        }
    }, [])

    useEffect(() => {
        const showStatementsInGraph = ({
            texts,
            uids,
            tags,
            nodes,
            edges,
            statementHashtags,
        }) => {
            handleShowStatements({
                texts,
                uids,
                tags,
                nodes,
                edges,
                statementHashtags,
            })
        }

        if (window.myEventEmitter) {
            myEventEmitter.on('showStatementsInGraph', showStatementsInGraph)
        }

        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener(
                    'showStatementsInGraph',
                    showStatementsInGraph
                )
            }
        }
    }, [])

    const [isAddButtonVisible, setIsAddButtonVisible] = useState(true)
    const [isTextareaVisible, setIsTextareaVisible] = useState(false)
    const [addNewStatementOpen, setAddNewStatementOpen] = useState(false)

    const addStatementRequested = useZustand(
        state => state.addStatementRequested
    )

    // Consume the empty-graph "Add statements" intent: reveal the add form
    // (collapsing the add button slides the textarea in via
    // AddNewStatementForm's onEnd handler), then clear the request — cleared
    // even when authoring is disabled, so a stale request can't fire later
    // on a different view.
    useEffect(() => {
        if (!addStatementRequested) return
        if (!isAuthoringDisabled) {
            setIsAddButtonVisible(false)
            setAddNewStatementOpen(true)
        } else {
            useZustand.getState().clearVoiceInputRequest()
        }
        useZustand.getState().clearAddStatementRequest()
    }, [addStatementRequested, isAuthoringDisabled])

    const [showOnlyHighlighted, setShowOnlyHighlighted] = useState(
        readShowFilteredOnlyFromStorage
    )

    useEffect(() => {
        try {
            localStorage.setItem(
                SHOW_FILTERED_ONLY_STORAGE_KEY,
                showOnlyHighlighted ? '1' : '0'
            )
        } catch {}
    }, [showOnlyHighlighted])

    const showSnackbar = useSnackbar(selectorShowSnackbar)

    const showOnlyHighlightedButtonVisible =
        useFilterStore.getState().isAnyHighlightActive() ||
        pinnedNodes.length !== 0

    const filterHashtags = useCallback(
        hashtags =>
            hashtags
                ?.filter(hashtag => pinnedNodes.includes(hashtag))
                .map(hashtag => hashtag),
        [pinnedNodes]
    )

    // const filteredStatements = useFilterStore.getState().filteredStatements
    const filteredStatements = useFilterStore(state => state.filteredStatements)

    const statementsForExtension = useMemo(() => {
        return filteredStatements.filter(
            statement => !statement.grayedOut && statement.visible
        )
    }, [filteredStatements])

    const statementsWithHighlightedWords = useMemo(
        () =>
            filteredStatements.map(statement => ({
                ...statement,
                wordsToHighlight: filterHashtags(statement.hashtags),
            })),
        [filteredStatements, filterHashtags]
    )

    const displayedStatements = useMemo(() => {
        if (!showOnlyHighlighted) return statementsWithHighlightedWords

        const visible = statementsWithHighlightedWords.filter(
            statement => !statement.grayedOut
        )

        // Rank only when pins drive the highlight. Other highlight filters
        // (sentiment/topic/tag) keep statements in natural order.
        if (pinnedNodes.length === 0) return visible

        return [...visible].sort((a, b) => {
            const aR = a.pinnedRanking || { count: 0, concentration: 0 }
            const bR = b.pinnedRanking || { count: 0, concentration: 0 }
            const dCount = bR.count - aR.count
            if (dCount !== 0) return dCount
            const dConc = bR.concentration - aR.concentration
            if (dConc !== 0) return dConc
            // Deterministic tiebreaker so equal (count, concentration) pairs
            // don't reorder when upstream traversal order shifts.
            return String(a.uid).localeCompare(String(b.uid))
        })
    }, [
        showOnlyHighlighted,
        statementsWithHighlightedWords,
        pinnedNodes.length,
    ])

    const {
        virtuosoRef,
        activeMatchUid,
        navigatorVisible,
        hasMatches,
        activeMatchIdx,
        matchCount,
        goToPrev,
        goToNext,
    } = useMatchNavigation({
        displayedStatements,
        showOnlyHighlighted,
        showOnlyHighlightedButtonVisible,
    })

    useEffect(() => {
        const statementsInAllContexts = useFilterStore.getState().statements

        setAllStatements(statementsInAllContexts)
    }, [filteredStatements])

    const numberOfDisplayedStatements = displayedStatements.length

    const percentOfAllStatements =
        allStatements && allStatements.length > 0
            ? `${Math.round(
                  (numberOfDisplayedStatements / allStatements.length) * 100
              )}%`
            : '100%'

    // TODO: move to FilterBridge
    const handleStatementSelect = useCallback(
        uid => {
            const resetHighlight = useFilterStore.getState()
                .resetHighlightFilters
            const isAnyHighlightActive = useFilterStore
                .getState()
                .isAnyHighlightActive()
            setSelectedStatements(prev => {
                const newSelected = new Set(prev)

                if (newSelected.has(uid)) {
                    newSelected.delete(uid)
                } else {
                    newSelected.add(uid)
                }

                if (isAnyHighlightActive) {
                    resetHighlight()
                }

                const selectedIds = Array.from(newSelected).map(uid =>
                    uid.toString()
                )
                if (selectedIds.length === 0) {
                    const allIds = filteredStatements.map(({ uid }) =>
                        uid.toString()
                    )
                    graph().selectEntries(allIds, 'noplay', true)
                    return newSelected
                }

                graph().selectEntries(selectedIds, 'noplay', true)
                return newSelected
            })

            sendToAnalytics({
                event: 'statements_action',
                data: {
                    userId: username,
                    action: 'select_statement',
                    source: 'statement',
                },
            })

            if (typeof posthog != 'undefined') {
                posthog.capture('statements_action', {
                    action: 'select_statement',
                })
            }
        },
        [filteredStatements]
    )

    const handleDeleteSelected = async () => {
        const isAnyFilterActive = useFilterStore.getState().isAnyFilterActive()
        const resetAllFilters = useFilterStore.getState().resetAllFilters
        console.log('Deleting statements:', Array.from(selectedStatements))
        const statementsToDelete = Array.from(selectedStatements)
        if (statementsToDelete.length === 0) {
            return
        }
        setSelectedStatements(new Set())

        if (myEventEmitter._events['onStatementsAction']) {
            myEventEmitter.emit('onStatementsAction', {
                action: 'delete_statement',
            })
        }

        const {
            entriesAndGraphOfContext,
        } = await useZustand.getState().removeStatements(statementsToDelete)

        // legacy sigma graph
        graph().deleteStatements(statementsToDelete)
        resetPinnedNodes()

        if (isAnyFilterActive) {
            resetAllFilters()
        }

        await recalculateCurrentGraph({
            precomputedGraphData: { entriesAndGraphOfContext },
        })

        const successString =
            statementsToDelete.length > 1
                ? `Statements have been successfully deleted`
                : `The statement has been successfully deleted`
        showSnackbar('success', successString)

        sendToAnalytics({
            event: 'statements_action',
            data: {
                userId: username,
                action: 'delete_statement',
                source: 'statement',
            },
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('statements_action', {
                action: 'delete_statement',
            })
        }
    }

    const toggleStatementsPanel = useCallback(
        event => {
            if (event.action === 'toggle_statements') {
                setIsOpen(prev => !prev)
            }
            if (event.action === 'force_open_statements') {
                setIsOpen(true)
            }
            if (event.action === 'force_close_statements') {
                setIsOpen(false)
            }
        },
        [setIsOpen]
    )

    useEffect(() => {
        if (window.myEventEmitter) {
            window.myEventEmitter.on(
                'onStatementsAction',
                toggleStatementsPanel
            )
        }

        return () => {
            if (window.myEventEmitter) {
                window.myEventEmitter.removeListener(
                    'onStatementsAction',
                    toggleStatementsPanel
                )
            }
        }
    }, [toggleStatementsPanel])

    const handleSave = async ({ text, tags, extractEntities, source }) => {
        const isAnyFilterActive = useFilterStore.getState().isAnyFilterActive()
        const resetAllFilters = useFilterStore.getState().resetAllFilters
        if (isAnyFilterActive) {
            resetAllFilters()
        }

        const {
            uid,
            entriesAndGraphOfContext,
        } = await useZustand
            .getState()
            .addNewStatement(text, tags, extractEntities)

        if (uid) {
            console.log('Statement saved successfully')

            // The statement just created a brand-new context (it didn't exist
            // when the page loaded — see bootstrapContextView's seeding path).
            // Promote it in place rather than reloading, so a running voice
            // dictation keeps its microphone. The flag is cleared before any
            // await so a save racing this one takes the plain path.
            const wasSeeded = useZustand.getState().seededEmptyContext
            if (wasSeeded) useZustand.getState().setSeededEmptyContext(false)
            const promotion = wasSeeded
                ? promoteSeededContext({
                      contextName: useZustand.getState().currentContext,
                      graphOwnerUsername: useBootstrapStore.getState()
                          .graphOwnerUsername,
                  })
                : null

            showSnackbar('success', `The statement has been successfully saved`)

            // reuse the graph from /add-and-recalculate (no refetch)
            if (entriesAndGraphOfContext) {
                await recalculateCurrentGraph({
                    precomputedGraphData: { entriesAndGraphOfContext },
                })
            }

            // Sync filteredStatements so the statement editor reflects the add
            const updatedCtx = useZustand.getState().currentContext
            const updatedStatements =
                useZustand.getState().statements[updatedCtx] || []
            useFilterStore.getState().setFilteredStatements(updatedStatements)

            // AI names come from a separate slow endpoint (see loadGraphData.js).
            applyAiTopicsToActiveGraph({ contextName: updatedCtx })

            // for user posting into chat mode
            if (
                source !== 'chat' &&
                userFactory &&
                userFactory.getGraphOption('chat') &&
                userFactory.getGraphOption('mode') == 'insight_chat'
            ) {
                aiFactory.submitToGPT3(
                    'chatgpt',
                    null,
                    text,
                    true,
                    'chat mode',
                    true
                )
            }
            // clear selection so the next click starts unselected
            setSelectedStatements(new Set())

            // Never rejects; waiting keeps the next dictated segment from
            // starting before the drawer knows the context.
            if (promotion) await promotion
        }
    }

    const handleShowStatements = async ({
        texts,
        uids,
        tags,
        nodes,
        edges,
        statementHashtags,
    }) => {
        const isAnyFilterActive = useFilterStore.getState().isAnyFilterActive()
        const resetAllFilters = useFilterStore.getState().resetAllFilters
        if (isAnyFilterActive) {
            resetAllFilters()
        }

        if (!uids || uids.length === 0) {
            return
        }
        console.log(statementHashtags)

        // Get currentContext directly from store to avoid stale closure
        // (the event listener is set up with empty deps array)
        const contextName = useZustand.getState().currentContext

        if (texts.length > 0) {
            addPrivateStatementsToStore(
                texts,
                contextName,
                uids,
                statementHashtags
            )
        }

        graphFactory.addNewNodes(nodes, edges, uids)
    }

    const handleAddButtonClick = () => {
        setIsAddButtonVisible(false) // First hide the add button
        setAddNewStatementOpen(true) // Update AddNewStatement state
    }

    const handleExternalAddNewStatement = () => {
        const newOpenState = !addNewStatementOpen
        setAddNewStatementOpen(newOpenState)

        if (!newOpenState) {
            handleTextareaClose()
        } else {
            setIsAddButtonVisible(false)
        }
    }

    const handleTextareaClose = () => {
        setIsTextareaVisible(false)
        setAddNewStatementOpen(false)
    }

    const getStatementContextLabel = statement => {
        const contextsState = useContextsStore.getState()
        if (
            (!isCompareMode && !isSynthesisMode && !isSearchMode) ||
            !contextsState.loadedContextList
        ) {
            return null
        }
        const record = findContextById(contextsState, statement.context)
        return record ? record.name : null
    }
    return (
        <Box
            sx={{
                backgroundColor: theme => theme.palette.background.dialogs,
                borderRadius: '14px',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'left',
                    padding: '8px 0px 4px 0px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    {autoAddFromAiChat && (
                        <Typography
                            variant="caption"
                            sx={{
                                width: '100%',
                                display: 'block',
                                opacity: 0.75,
                                mb: 0.5,
                            }}
                        >
                            AI Chat Mode: Auto Add Messages to Graph
                        </Typography>
                    )}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ width: '100%', display: 'block', mb: 0.25 }}
                    >
                        Source text
                        {sourceLabel ? ` · ${sourceLabel}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                        {numberOfDisplayedStatements} shown (
                        {percentOfAllStatements})
                    </Typography>
                    <Button
                        startIcon={<SelectAllIcon />}
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => {
                            const allIds = displayedStatements.map(
                                ({ uid }) => uid
                            )
                            const allIdsString = displayedStatements.map(
                                ({ uid }) => uid.toString()
                            )
                            if (selectedStatements.size !== 0) {
                                graph().selectEntries([], 'noplay', true)
                                setSelectedStatements(new Set())
                            } else {
                                graph().selectEntries(
                                    allIdsString,
                                    'noplay',
                                    true
                                )
                                setSelectedStatements(new Set(allIds))
                            }
                        }}
                    >
                        {selectedStatements.size === 0
                            ? 'select all'
                            : 'deselect all'}
                    </Button>
                </Box>
                <IconButton
                    size="small"
                    fontSize="small"
                    sx={{
                        position: 'absolute',
                        right: '8px',
                        color: theme => theme.palette.colors.icons,
                    }}
                    onClick={() => setIsOpen()}
                >
                    <HighlightOffIcon />
                </IconButton>
            </div>
            {showOnlyHighlightedButtonVisible && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pr: 1,
                    }}
                >
                    <FormGroup sx={{ ml: 2.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showOnlyHighlighted}
                                    size="small"
                                    onChange={() => {
                                        setShowOnlyHighlighted(
                                            !showOnlyHighlighted
                                        )
                                    }}
                                />
                            }
                            label="showing filtered only"
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '14px',
                                },
                            }}
                        />
                    </FormGroup>
                    <AnimatePresence initial={false}>
                        {navigatorVisible && (
                            <motion.div
                                key="match-navigator"
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -8 }}
                                transition={{
                                    duration: 0.15,
                                    ease: 'easeOut',
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <MatchNavigator
                                    current={
                                        hasMatches ? activeMatchIdx + 1 : 0
                                    }
                                    total={matchCount}
                                    onPrev={goToPrev}
                                    onNext={goToNext}
                                    disabled={!hasMatches}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: '324px',
                    // TODO: move this out of here to the parent container
                    // 100dvh tracks the *visible* viewport (mobile URL bar
                    // showing/hiding, rotation), so the footer stays on
                    // screen — a one-shot innerHeight measurement doesn't.
                    height: 'max(200px, calc(100dvh - 296px))',
                    backgroundColor: theme => theme.palette.background.dialogs,
                }}
            >
                <StatementList
                    virtuosoRef={virtuosoRef}
                    activeMatchUid={activeMatchUid}
                    statements={displayedStatements}
                    selectedStatements={selectedStatements}
                    handleStatementSelect={handleStatementSelect}
                    getStatementContextLabel={getStatementContextLabel}
                    isAddButtonVisible={isAddButtonVisible}
                    handleAddButtonClick={handleAddButtonClick}
                    isTextareaVisible={isTextareaVisible}
                    setIsTextareaVisible={setIsTextareaVisible}
                    setIsAddButtonVisible={setIsAddButtonVisible}
                    handleSave={handleSave}
                    handleTextareaClose={handleTextareaClose}
                    isAuthoringDisabled={isAuthoringDisabled}
                />
                <Footer
                    onOpen={handleExternalAddNewStatement}
                    open={addNewStatementOpen}
                    selectedStatements={selectedStatements}
                    handleDeleteSelected={handleDeleteSelected}
                    isAuthoringDisabled={isAuthoringDisabled}
                />
            </div>
            <div id="entries">
                {statementsForExtension.map(statement => (
                    <div key={statement.uid} className="entry-text hidden">
                        {statement.text}
                    </div>
                ))}
            </div>
        </Box>
    )
}

export default StatementEditor
